import {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
  isAxiosError,
} from 'axios';

import { fromQueryString, parseUrl, toQueryString } from '@suntex.core/utils';

import { ApiException, Client } from './api-proxy';

enum HttpStatus {
  OK = 200,
  NO_CONTENT = 204,
}

export class ApiClient extends Client {
  constructor(baseUrl: string, instance: AxiosInstance) {
    super(baseUrl, instance);
  }

  get<T>(url: string, cancelToken?: CancelToken): Promise<T | null> {
    const finalUrl = this.buildUrlWithApiVersion(url, 'GET');
    const options: AxiosRequestConfig = {
      method: 'GET',
      url: finalUrl,
      headers: {
        Accept: 'text/plain',
      },
      cancelToken,
    };

    return this.executeRequest<T>(options);
  }

  post<T>(
    url: string,
    data: Record<string, unknown>,
    cancelToken?: CancelToken,
  ): Promise<T | null> {
    const finalUrl = this.buildUrlWithApiVersion(url, 'POST');
    const options: AxiosRequestConfig = {
      method: 'POST',
      url: finalUrl,
      data,
      headers: {
        Accept: 'text/plain',
      },
      cancelToken,
    };

    return this.executeRequest<T>(options);
  }

  protected processResult<T>(response: AxiosResponse<string>): T | null {
    const status = response.status;
    const _headers: Record<string, unknown> = {};
    for (const k in response.headers) {
      if (response.headers[k]) {
        _headers[k] = response.headers[k];
      }
    }

    const _responseText = response.data;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (status === HttpStatus.OK) {
      //if we got a response
      if (_responseText.trim() !== '') {
        //parse and return
        return JSON.parse(_responseText) as T;
      }
      //no data
      else {
        //return null
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    } else if (status === HttpStatus.NO_CONTENT) {
      return null;
    } else {
      throw new ApiException(
        'An unexpected server error occurred.',
        status,
        _responseText,
        _headers,
        null,
      );
    }
  }

  /**
   * Executes the HTTP request with common error handling and response processing
   */
  private executeRequest<T>(options: AxiosRequestConfig): Promise<T | null> {
    return this.instance
      .request(options)
      .catch((_error: unknown) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse<string>) => {
        return this.processResult<T>(_response);
      });
  }

  /**
   * Validates API version and builds the complete URL with API version parameter
   */
  private buildUrlWithApiVersion(url: string, method: 'GET' | 'POST'): string {
    const apiVersion = Client.DEFAULT_API_VERSION;
    let finalUrl: string;

    if (method === 'GET') {
      // For GET requests, parse existing query parameters and add api-version
      const parse = parseUrl(url);
      const qs = fromQueryString(parse.query);
      qs['api-version'] = apiVersion;
      finalUrl = `${parse.base}?${toQueryString(qs)}`;
    } else {
      // For POST requests, simply append api-version as query parameter
      const qs: Record<string, string> = { 'api-version': apiVersion };
      finalUrl = `${url}?${toQueryString(qs)}`;
    }

    // Build complete URL with base URL
    let completeUrl = this.baseUrl + finalUrl;

    // Clean up trailing ? or & characters
    completeUrl = completeUrl.replace(/[?&]$/, '');

    return completeUrl;
  }
}
